import React, { ReactElement } from 'react';
import { connect } from 'react-redux';

import SummaryColumn from '@/components/SummaryColumn';
import { getBpcPayload } from '@/selectors/bpc';

import { FieldType, EncryptedString } from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { FIELD_OVERRIDES } from '@/bpc/overrides/fields';
import { FieldOverride } from '@/bpc/types/FieldOverride';
import { BusinessSizeLabel } from '@/bpc/overrides/fields/BusinessSizeComponent/BusinessSizeLabel';
import { BusinessSize } from '@mjolnir/mf-fields-ts/src/enm/BusinessSize';
import { BusinessSizeConfig } from '@mjolnir/mf-fields-ts/src/types/BusinessSizeConfig';

const tPrefixGeneral = 'products-fe.general';

type SummaryFieldProps = {
  field: OnboardingField;
  t: TranslatorFunction;
  value: boolean | number | string | EncryptedString;
};

export const _OnboardingSummaryFieldComponent: React.FC<SummaryFieldProps> = ({
  field,
  t,
  value,
}: SummaryFieldProps): ReactElement | null => {
  const contentOverride = (): ReactElement | undefined => {
    const fieldOverride = FIELD_OVERRIDES[field.absolutePath];
    if (fieldOverride === FieldOverride.BusinessSizeComponent) {
      const { currency, tiers = {}, rate } = ((field.customConfiguration() ||
        {}) as unknown) as BusinessSizeConfig;
      const { from, to } = tiers[BusinessSize[value as BusinessSize]] || {};
      return <BusinessSizeLabel currency={currency} from={from} to={to} rate={rate} t={t} />;
    }
  };

  const content = (): string => {
    if (value == null) {
      return '-';
    }

    switch (field.type) {
      case FieldType.Boolean:
        return value ? t(`${tPrefixGeneral}.yes`) : t(`${tPrefixGeneral}.no`);
      case FieldType.Enum:
        return (
          t(field.labelForEnumValue(value as string)) ||
          field.fallbackLabelForEnumValue(value as string)
        );
      case FieldType.ArrayOfEnum:
        return (value as FieldType.Enum[])
          .map(
            element =>
              t(field.labelForEnumValue(element)) || field.fallbackLabelForEnumValue(element),
          )
          .join(', ');
      case FieldType.EncryptedString:
        return t(`${tPrefixGeneral}.encrypted_text`);
    }

    return value.toString();
  };

  if (!field.applicable()) {
    return null;
  }

  return (
    <SummaryColumn
      key={field.absolutePath}
      title={t(field.label, {}, field.fallbackLabel)}
      content={contentOverride() || content()}
    />
  );
};

export const OnboardingSummaryFieldComponent = connect(state => ({
  payload: getBpcPayload(state),
}))(_OnboardingSummaryFieldComponent);
